import { useState } from 'react';
import { toast } from 'react-toastify';
import { stanNotify } from '@/lib';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { useUser } from '@/queries/useUser';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useDeleteFlow, useDuplicateFlow } from '../../queries';
import type { FlowSummary } from '@/lib/api/email-flows/types';

export function useBroadcastActions() {
    const { user } = useUser();
    const { push } = useStanRouter();

    const [deletingBroadcastId, setDeletingBroadcastId] = useState<number | null>(null);

    // Use mutations from queries folder
    const duplicateBroadcastMutation = useDuplicateFlow();
    const deleteBroadcastMutation = useDeleteFlow();

    const navigateToBroadcastEditor = (broadcast: FlowSummary) => {
        const emailId = broadcast.flow_emails?.[0]?.flow_email_id;
        if (emailId) {
            push(`/flows/${broadcast.flow_id}/broadcast/emails/${emailId}`);
        } else {
            push(`/flows/${broadcast.flow_id}/broadcast/new`);
        }
    };

    const handleEdit = (broadcast: FlowSummary) => {
        navigateToBroadcastEditor(broadcast);
    };

    const handleDuplicate = (flowId: number) => {
        // Show loading toast immediately
        stanNotify({
            type: 'info',
            title: 'Duplicating broadcast...',
            duration: 10000,
        });

        duplicateBroadcastMutation.mutate(flowId, {
            onSuccess: data => {
                // Dismiss the loading toast
                toast.dismiss();

                stanNotify({
                    type: 'success',
                    title: 'Done!',
                    text: 'Your broadcast has been duplicated.',
                });

                stanAnalytics('flow-duplicated', {
                    meta: { user_id: user?.user_id, username: user?.username },
                    props: { flow_id: data.flow_id },
                });
            },
            onError: () => {
                // Dismiss the loading toast
                toast.dismiss();

                stanNotify({
                    type: 'error',
                    title: 'Something went wrong',
                    text: 'Please contact us at friends@stanwith.me',
                });
            },
        });
    };

    const confirmDelete = async (flowId: number | null, onClose: () => void) => {
        if (flowId) {
            onClose();

            // Set deleting state to trigger animation
            setDeletingBroadcastId(flowId);

            // Wait for animation to complete
            await new Promise(resolve => setTimeout(resolve, 550));

            // Actually delete the broadcast
            deleteBroadcastMutation.mutate(flowId, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Broadcast Deleted',
                        text: 'Your broadcast has been permanently deleted.',
                    });
                },
                onError: () => {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: 'Please contact us at friends@stanwith.me',
                    });
                    setDeletingBroadcastId(null);
                },
            });
        }
    };

    const handleViewRecipients = (broadcast: FlowSummary) => {
        // Broadcasts have only one email, so we can safely access the first email
        if (broadcast.flow_emails && broadcast.flow_emails[0]) {
            push(`/flows/recipients/${broadcast.flow_emails[0].flow_email_id}`);
        }
    };

    return {
        navigateToBroadcastEditor,
        handleEdit,
        handleDuplicate,
        handleViewRecipients,
        confirmDelete,
        deletingBroadcastId,
        isDuplicating: duplicateBroadcastMutation.isPending,
        isDeleting: deleteBroadcastMutation.isPending,
    };
}
